// src/utilities/passkeyAuth.js
import {
  startRegistration,
  startAuthentication,
} from "@simplewebauthn/browser";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

/**
 * Get registration options from backend
 * @param {string} username - User's username
 * @returns {Promise} - Registration options
 */
export const getRegistrationOptions = async (username) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/users/passkey/register-options`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to get registration options");
    }
    
    return await response.json();
  } catch (error) {
    console.error("Error getting registration options:", error);
    throw error;
  }
};

/**
 * Register a passkey for the user
 * @param {string} username - User's username
 * @returns {Promise} - Success message
 */
export const registerPasskey = async (username) => {
  try {
    // Step 1: Get registration options from backend
    const options = await getRegistrationOptions(username);
    console.log("Registration options from backend:", JSON.stringify(options, null, 2));

    // Step 2: Start registration with browser
    const registrationResponse = await startRegistration(options);
    console.log("Browser registration response:", JSON.stringify(registrationResponse, null, 2));

    // Step 3: Send registration response to backend for verification
    const verifyResponse = await fetch(
      `${API_BASE_URL}/api/users/passkey/register-verify`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          registrationResponse,
        }),
      }
    );

    const verifyData = await verifyResponse.json();
    console.log("Register-verify response:", JSON.stringify(verifyData, null, 2));

    if (!verifyResponse.ok) {
      throw new Error(verifyData.message || "Passkey registration failed");
    }

    return verifyData;
  } catch (error) {
    console.error("Passkey registration error:", error);
    throw error;
  }
};

/**
 * Get authentication options from backend
 * @param {string} username - User's username
 * @returns {Promise} - Authentication options
 */
export const getAuthenticationOptions = async (username) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/users/passkey/login-options`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to get authentication options");
    }

    const options = await response.json();
    console.log("Authentication options from backend:", JSON.stringify(options, null, 2));
    console.log("allowCredentials count:", options.allowCredentials?.length ?? 0);

    // enforce platform authenticator paths just in case backend sent cross-platform transports
    if (options.allowCredentials && Array.isArray(options.allowCredentials)) {
      options.allowCredentials = options.allowCredentials.map((c) => ({
        ...c,
        transports: ["internal"],
      }));
    }

    if (!options.userVerification) {
      options.userVerification = "preferred"; // allow biometric/PIN
    }

    return options;
  } catch (error) {
    console.error("Error getting authentication options:", error);
    throw error;
  }
};

/**
 * Authenticate user with passkey
 * @param {string} username - User's username
 * @returns {Promise} - Login response with JWT token
 */
export const authenticateWithPasskey = async (username) => {
  try {
    // Step 1: Get authentication options from backend
    const options = await getAuthenticationOptions(username);

    // if backend returned no credentials, bail out early
    if (!options.allowCredentials || options.allowCredentials.length === 0) {
      throw new Error("No passkey registered for this user");
    }

    // Step 2: Start authentication with browser
    const authenticationResponse = await startAuthentication(options);

    // Step 3: Send authentication response to backend for verification
    const verifyResponse = await fetch(
      `${API_BASE_URL}/api/users/passkey/login-verify`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          authenticationResponse,
        }),
      }
    );

    if (!verifyResponse.ok) {
      const errorData = await verifyResponse.json();
      throw new Error(errorData.message || "Passkey authentication failed");
    }

    return await verifyResponse.json();
  } catch (error) {
    console.error("Passkey authentication error:", error);
    throw error;
  }
};

/**
 * Check if browser supports passkey registration/authentication
 * @returns {boolean}
 */
export const isPasskeySupported = () => {
  return (
    window.PublicKeyCredential !== undefined &&
    navigator.credentials !== undefined
  );
};
